import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Controller, useForm } from 'react-hook-form';
import { ContractorDto } from '@vatos-pas/common';

import {
  AsyncFieldWrapper,
  Autocomplete,
  FormToolbar,
  SnackbarContainer,
} from 'components/standalone';
import { useRefreshTrigger } from 'hooks';
import { createValidationResolver } from 'utils';
import { PermissionsProvider } from 'providers/permissionsProvider';
import { AppRole } from 'providers/roles';

import {
  BumperOption,
  ContractorFormValues,
  RegionOption,
} from '../containers';
import { validateContractor } from '../views/contractorValidation';
import { bumperPaymentTypeOptions, paymentTypeOptions } from '../views/constants';
import { BumperField } from './BumperField';
import { ResourceTypeTable } from './ResourceTypeTable';

export interface ContractorFormProps {
  mode: 'create' | 'edit';
  initialValues?: ContractorDto | null;
  initialRegionIds?: string[];
  initialBumperIds?: string[];
  onSubmit: (values: ContractorFormValues) => Promise<void>;
  loading: boolean;
  onGoBack: () => void;
  loadRegionOptions: (searchTerm: string) => Promise<RegionOption[]>;
  loadBumperOptions: (contractorId: string) => Promise<BumperOption[]>;
  regionsLoading?: boolean;
  bumpersLoading?: boolean;
  regionsError?: string | null;
  bumpersError?: string | null;
  roundTripFormState?: Partial<ContractorFormValues> | null;
  roundTripSelectedBumpers?: string[] | null;
  roundTripBumperOption?: BumperOption | null;
  /**
   * When true, all "detail" inputs (name, corporation, payments, regions,
   * notes, active/suspended/high-ceiling toggles, bumpers) are read-only.
   * Resource type table stays editable so a Regional Manager
   * (CAN_EDIT_CONTRACTOR_CAPACITY only) can still update capacity.
   */
  cannotEditDetails?: boolean;
}

const parseNullableNumber = (raw: string): number | null =>
  raw === '' ? null : Number(raw);

const getDefaultValues = (
  initialValues?: ContractorDto | null,
  initialRegionIds: string[] = [],
  initialBumperIds: string[] = [],
): ContractorFormValues => ({
  name: initialValues?.name ?? '',
  corporation: initialValues?.corporation ?? '',
  paymentType: initialValues?.paymentType ?? '',
  paymentRate: initialValues?.paymentRate ?? null,
  bumperPaymentType: initialValues?.bumperPaymentType ?? '',
  bumperPaymentRate: initialValues?.bumperPaymentRate ?? null,
  hangerAvailable: initialValues?.hangerAvailable ?? false,
  finisherAvailable: initialValues?.finisherAvailable ?? false,
  sprayerAvailable: initialValues?.sprayerAvailable ?? false,
  trimAvailable: initialValues?.trimAvailable ?? false,
  paintAvailable: initialValues?.paintAvailable ?? false,
  bumperAvailable: initialValues?.bumperAvailable ?? false,
  specialistAvailable: initialValues?.specialistAvailable ?? false,
  hangerCapacity: initialValues?.hangerCapacity ?? null,
  finisherCapacity: initialValues?.finisherCapacity ?? null,
  sprayerCapacity: initialValues?.sprayerCapacity ?? null,
  trimCapacity: initialValues?.trimCapacity ?? null,
  paintCapacity: initialValues?.paintCapacity ?? null,
  bumperCapacity: initialValues?.bumperCapacity ?? null,
  active: initialValues?.active ?? true,
  suspended: initialValues?.suspended ?? false,
  canPerformHighCeilingTasks: initialValues?.canPerformHighCeilingTasks ?? false,
  notes: initialValues?.notes ?? '',
  selectedRegions: initialRegionIds,
  selectedBumpers: initialBumperIds,
});

export const ContractorForm: React.FC<ContractorFormProps> = ({
  mode,
  initialValues,
  initialRegionIds = [],
  initialBumperIds = [],
  onSubmit,
  loading,
  onGoBack,
  loadRegionOptions,
  loadBumperOptions,
  regionsLoading = false,
  bumpersLoading = false,
  regionsError = null,
  bumpersError = null,
  roundTripFormState = null,
  roundTripSelectedBumpers = null,
  roundTripBumperOption = null,
  cannotEditDetails = false,
}) => {
  const classes = useStyles();
  const isCreateMode = mode === 'create';
  const { notify } = SnackbarContainer.useContainer();

  const [regionOptionsCache, setRegionOptionsCache] = useState<RegionOption[]>(
    [],
  );
  const [bumperOptionsCache, setBumperOptionsCache] = useState<BumperOption[]>(
    [],
  );
  const [refreshKey, setRefreshKey] = useState(0);

  useRefreshTrigger(() => {
    setRegionOptionsCache([]);
    setBumperOptionsCache([]);
    setRefreshKey(k => k + 1);
  });

  const baseDefaults = useMemo(
    () => getDefaultValues(initialValues, initialRegionIds, initialBumperIds),
    [initialValues, initialRegionIds, initialBumperIds],
  );

  const mergedDefaults = useMemo(
    () => ({
      ...baseDefaults,
      ...(roundTripFormState ?? {}),
      ...(roundTripSelectedBumpers
        ? { selectedBumpers: roundTripSelectedBumpers }
        : {}),
    }),
    [baseDefaults, roundTripFormState, roundTripSelectedBumpers],
  );

  const formValues = useMemo(
    () => (initialValues ? mergedDefaults : undefined),
    [initialValues, mergedDefaults],
  );

  useEffect(() => {
    if (!roundTripBumperOption) return;
    setBumperOptionsCache(prev => {
      if (prev.some(opt => opt.id === roundTripBumperOption.id)) return prev;
      return [roundTripBumperOption, ...prev];
    });
  }, [roundTripBumperOption]);

  const { hasRole } = PermissionsProvider.useContainer();
  const actorIsRegionalAdmin =
    hasRole(AppRole.RegionalAdmin) && !hasRole(AppRole.SuperAdmin);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
    formState: { errors, isSubmitted },
  } = useForm<ContractorFormValues>({
    defaultValues: mergedDefaults,
    values: formValues,
    resetOptions: { keepDirtyValues: true },
    resolver: createValidationResolver<ContractorFormValues>(values =>
      validateContractor(values, { requireRegion: actorIsRegionalAdmin }),
    ),
    mode: 'onBlur',
  });

  const onFormSubmit = useMemo(
    () =>
      handleSubmit(onSubmit, () => {
        notify('The form is not valid. Please check for errors', 'error');
      }),
    [handleSubmit, onSubmit, notify],
  );

  const handleLoadRegionOptions = useCallback(
    async (searchTerm: string): Promise<RegionOption[]> => {
      const options = await loadRegionOptions(searchTerm);
      setRegionOptionsCache(options);
      return options;
    },
    [loadRegionOptions],
  );

  const handleLoadBumperOptions = useCallback(
    async (contractorId: string): Promise<BumperOption[]> => {
      const options = await loadBumperOptions(contractorId);
      setBumperOptionsCache(options);
      return options;
    },
    [loadBumperOptions],
  );

  const activeValue = watch('active');

  return (
    <Box>
      <Box className={classes.formContent}>
        <Box className={classes.fields}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                className={classes.input}
                label="Name"
                error={!!errors.name}
                helperText={errors.name?.message}
                margin="dense"
                variant="filled"
                disabled={cannotEditDetails}
                inputProps={{ 'data-cy': 'contractorFormNameInput' }}
              />
            )}
          />
          <Controller
            name="corporation"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                className={classes.input}
                label="Corporation"
                error={!!errors.corporation}
                helperText={errors.corporation?.message}
                margin="dense"
                variant="filled"
                disabled={cannotEditDetails}
                inputProps={{ 'data-cy': 'contractorFormCorporationInput' }}
              />
            )}
          />
        </Box>

        <Divider className={classes.divider} />

        <Box className={classes.fields}>
          <Controller
            name="paymentType"
            control={control}
            render={({ field }) => (
              <FormControl
                fullWidth
                className={classes.input}
                variant="filled"
                margin="dense"
                error={!!errors.paymentType}
                disabled={cannotEditDetails}
              >
                <InputLabel>Payment Type</InputLabel>
                <Select
                  {...field}
                  value={field.value || ''}
                  inputProps={{ 'data-cy': 'contractorFormPaymentTypeSelect' }}
                >
                  {paymentTypeOptions.map(option => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.paymentType && (
                  <FormHelperText>{errors.paymentType.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
          <Controller
            name="paymentRate"
            control={control}
            render={({ field }) => (
              <TextField
                fullWidth
                className={classes.input}
                label="Payment Rate"
                type="number"
                value={field.value ?? ''}
                onBlur={field.onBlur}
                onChange={event =>
                  field.onChange(parseNullableNumber(event.target.value))
                }
                inputRef={field.ref}
                error={!!errors.paymentRate}
                helperText={errors.paymentRate?.message}
                margin="dense"
                variant="filled"
                disabled={cannotEditDetails}
                inputProps={{
                  min: 1,
                  max: 100,
                  step: 0.01,
                  'data-cy': 'contractorFormPaymentRateInput',
                }}
              />
            )}
          />
          <Controller
            name="bumperPaymentType"
            control={control}
            render={({ field }) => (
              <FormControl
                fullWidth
                className={classes.input}
                variant="filled"
                margin="dense"
                error={!!errors.bumperPaymentType}
                disabled={cannotEditDetails}
              >
                <InputLabel>Bumper Payment Type</InputLabel>
                <Select
                  {...field}
                  value={field.value ?? ''}
                  inputProps={{
                    'data-cy': 'contractorFormBumperPaymentTypeSelect',
                  }}
                >
                  {bumperPaymentTypeOptions.map(option => (
                    <MenuItem key={option.id || 'na'} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.bumperPaymentType && (
                  <FormHelperText>
                    {errors.bumperPaymentType.message}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />
          <Controller
            name="bumperPaymentRate"
            control={control}
            render={({ field }) => (
              <TextField
                fullWidth
                className={classes.input}
                label="Bumper Payment Rate"
                type="number"
                value={field.value ?? ''}
                onBlur={field.onBlur}
                onChange={event =>
                  field.onChange(parseNullableNumber(event.target.value))
                }
                inputRef={field.ref}
                error={!!errors.bumperPaymentRate}
                helperText={errors.bumperPaymentRate?.message}
                margin="dense"
                variant="filled"
                disabled={cannotEditDetails}
                inputProps={{
                  min: 1,
                  max: 100,
                  step: 0.01,
                  'data-cy': 'contractorFormBumperPaymentRateInput',
                }}
              />
            )}
          />
        </Box>

        <Box className={classes.fields} my={3}>
          <Controller
            name="selectedRegions"
            control={control}
            render={({ field, fieldState: { error } }) => {
              const regionObjects = (field.value ?? [])
                .map(id => regionOptionsCache.find(r => r.id === id))
                .filter((r): r is RegionOption => !!r);
              return (
                <Box className={classes.halfWidthField}>
                  <AsyncFieldWrapper loading={regionsLoading}>
                    <Autocomplete<RegionOption>
                      key={`regions-${refreshKey}`}
                      disableCloseOnSelect
                      multiple
                      clientSideFilter
                      label="Regions"
                      loadOptions={handleLoadRegionOptions}
                      loadTrigger={isCreateMode ? 'focus' : 'mount'}
                      loading={regionsLoading}
                      value={regionObjects}
                      onChange={newValue => {
                        // Links the user cannot see (no loaded option) must survive every change.
                        const hidden = (field.value ?? []).filter(
                          (id: string) => !regionObjects.some(o => o.id === id),
                        );
                        field.onChange([...hidden, ...newValue.map(r => r.id)]);
                      }}
                      getOptionLabel={option => option.name}
                      getOptionValue={option => option.id}
                      error={error?.message ?? regionsError ?? undefined}
                      data-cy="contractorFormRegionsAutocomplete"
                      fullWidth
                      disabled={cannotEditDetails}
                    />
                  </AsyncFieldWrapper>
                </Box>
              );
            }}
          />
          <Controller
            name="notes"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                className={classes.halfWidthField}
                label="Notes"
                error={!!errors.notes}
                helperText={errors.notes?.message}
                margin="dense"
                variant="filled"
                multiline
                rows={2}
                disabled={cannotEditDetails}
                inputProps={{ 'data-cy': 'contractorFormNotesInput' }}
              />
            )}
          />
        </Box>

        {!isCreateMode && (
          <Box className={classes.fullWidth} mb={3}>
            <BumperField
              contractorId={initialValues?.id}
              control={control}
              watch={watch}
              getValues={getValues}
              loadBumperOptions={handleLoadBumperOptions}
              bumperOptionsCache={bumperOptionsCache}
              bumpersLoading={bumpersLoading}
              bumpersError={bumpersError}
              disabled={cannotEditDetails}
            />
          </Box>
        )}

        <Box className={classes.fieldsStart}>
          {!isCreateMode && (
            <Controller
              name="active"
              control={control}
              render={({ field: { onChange, value, ref } }) => (
                <FormControlLabel
                  className={classes.input}
                  disabled={cannotEditDetails}
                  control={
                    <Switch
                      checked={!!value}
                      onChange={event => onChange(event.target.checked)}
                      inputRef={ref}
                      color="primary"
                      disabled={cannotEditDetails}
                      inputProps={{
                        'aria-label': 'Active',
                      }}
                    />
                  }
                  label="Active"
                  data-cy="contractorFormActiveSwitch"
                />
              )}
            />
          )}
          <Controller
            name="suspended"
            control={control}
            render={({ field: { onChange, value, ref } }) => (
              <FormControlLabel
                className={classes.input}
                disabled={cannotEditDetails}
                control={
                  <Switch
                    checked={!!value}
                    onChange={event => onChange(event.target.checked)}
                    inputRef={ref}
                    color="primary"
                    disabled={cannotEditDetails}
                  />
                }
                label="Suspended"
                data-cy="contractorFormSuspendedSwitch"
              />
            )}
          />
          <Controller
            name="canPerformHighCeilingTasks"
            control={control}
            render={({ field: { onChange, value, ref } }) => (
              <FormControlLabel
                className={classes.input}
                disabled={cannotEditDetails}
                control={
                  <Switch
                    checked={!!value}
                    onChange={event => onChange(event.target.checked)}
                    inputRef={ref}
                    color="primary"
                    disabled={cannotEditDetails}
                  />
                }
                label="Scaffolding - Can Perform High Ceiling Tasks"
                data-cy="contractorFormHighCeilingSwitch"
              />
            )}
          />
        </Box>
        {!isCreateMode && !activeValue && (
          <Box display="flex" minWidth="100%" px={2}>
            <Typography
              color="error"
              data-cy="contractorFormDeactivationWarning"
            >
              Are you sure you wish to remove this contractor as an active
              contractor? This will remove this contractor from the Contractor
              List and Move them to the Inactive Filter.
            </Typography>
          </Box>
        )}

        <Divider className={classes.divider} />

        <Box className={classes.tableWrapper}>
          <ResourceTypeTable
            control={control}
            watch={watch}
            setValue={setValue}
            trigger={trigger}
            errors={errors}
            isSubmitted={isSubmitted}
          />
        </Box>
      </Box>

      <FormToolbar
        onGoBack={onGoBack}
        onSave={onFormSubmit}
        saveLoading={loading}
        showSave
      />
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  formContent: {
    padding: theme.spacing(2),
  },
  input: {
    margin: theme.spacing(0, 1.875),
  },
  fields: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  fieldsStart: {
    width: '100%',
    display: 'flex',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  halfWidthField: {
    width: '50%',
    margin: theme.spacing(0, 1.875),
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      margin: theme.spacing(1, 1.875),
    },
  },
  fullWidth: {
    width: '100%',
  },
  tableWrapper: {
    width: '100%',
    display: 'flex',
  },
  divider: {
    width: '100%',
    margin: theme.spacing(2, 0, 4, 0),
  },
}));

export default ContractorForm;
